"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"

/**
 * Logs de debug independentes do Copilot.
 * Ativo quando NODE_ENV === "development" ou NEXT_PUBLIC_DEBUG_APP === "true".
 * Filtre no console por [AppDebug].
 */
function useAppDebugLog() {
  const pathname = usePathname()
  const mounted = useRef(false)

  const enabled =
    typeof window !== "undefined" &&
    (process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_DEBUG_APP === "true")

  useEffect(() => {
    if (!enabled) return

    if (!mounted.current) {
      mounted.current = true
      console.warn("[AppDebug] Layout mount", {
        pathname,
        NODE_ENV: process.env.NODE_ENV,
        NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
        hasWindow: typeof window !== "undefined",
        timestamp: new Date().toISOString(),
      })
    }
  }, [enabled, pathname])

  useEffect(() => {
    if (!enabled || !mounted.current) return
    console.warn("[AppDebug] Route change", { pathname, timestamp: new Date().toISOString() })
  }, [pathname, enabled])
}

export function AppDebugLog() {
  useAppDebugLog()
  return null
}

/**
 * Registra um handler global de erro para logar com [AppDebug] quando qualquer erro não tratado ocorrer.
 * Assim vemos o estado no momento do erro (pathname, etc.) sem depender do Copilot.
 */
export function useAppDebugGlobalError() {
  const enabled =
    typeof window !== "undefined" &&
    (process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_DEBUG_APP === "true")
  const pathname = usePathname()

  useEffect(() => {
    if (!enabled) return

    const handleError = (event: ErrorEvent) => {
      console.warn("[AppDebug] Global error", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        pathname,
        NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
        timestamp: new Date().toISOString(),
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      const message = reason?.message ?? String(reason)
      console.warn("[AppDebug] Unhandled rejection", {
        message,
        pathname,
        NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
        timestamp: new Date().toISOString(),
      })
    }

    window.addEventListener("error", handleError)
    window.addEventListener("unhandledrejection", handleUnhandledRejection)
    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleUnhandledRejection)
    }
  }, [enabled, pathname])
}

export function AppDebugGlobalErrorListener() {
  useAppDebugGlobalError()
  return null
}

/** Inclui os dois: log de mount/rota e listener de erro global. Use no layout. */
export function AppDebug() {
  return (
    <>
      <AppDebugLog />
      <AppDebugGlobalErrorListener />
    </>
  )
}
